import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandInput, CommandList } from "@/components/ui/command";
import { useSearchEverywhereData } from "./useSearchEverywhereData";
import { SearchResultRow } from "./SearchResultRow";

interface SearchEverywhereModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  maxResultsPerGroup?: number;
}

export function SearchEverywhereModal({
  open,
  onOpenChange,
  maxResultsPerGroup: maxPerGroup = 5,
}: SearchEverywhereModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="gap-0 overflow-hidden p-0 w-auto min-w-md max-w-2xl top-24 translate-y-0"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Quick Find</DialogTitle>
        {open && <SearchEverywhereContent maxPerGroup={maxPerGroup} onOpenChange={onOpenChange} />}
      </DialogContent>
    </Dialog>
  );
}

interface SearchEverywhereContentProps {
  maxPerGroup: number;
  onOpenChange: (open: boolean) => void;
}

function SearchEverywhereContent({ maxPerGroup, onOpenChange }: SearchEverywhereContentProps) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { results, isLoading } = useSearchEverywhereData(query, maxPerGroup);

  const handleSelect = useCallback(
    (route: string) => {
      navigate(route);
      onOpenChange(false);
    },
    [navigate, onOpenChange],
  );

  return (
    <Command shouldFilter={false} className="**:data-[slot=command-input-wrapper]:h-12">
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Search across projects, masks, spec lists, test labels, and more"
        suffix={
          isLoading && (
            <div className="animate-spin rounded-full border-2 border-muted border-t-foreground size-4 shrink-0" />
          )
        }
      />
      <CommandList className="max-h-96">
        {isLoading && !results.length && <div className="flex justify-center py-6">Loading...</div>}
        {!isLoading && (
          <CommandEmpty className="py-10 text-sm text-muted-foreground flex justify-center items-center">
            {query ? "No results for given query" : "No data found"}
          </CommandEmpty>
        )}
        {results.map((item) => (
          <SearchResultRow key={item.id} item={item} onSelect={handleSelect} />
        ))}
      </CommandList>
    </Command>
  );
}
